import { memo, useMemo } from "react";
import { FilterPills } from "./FilterPills";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import {
  ChatArtifactProvider,
  IChatArtifactContext,
  chatArtifactComponents,
  userSchema,
} from "./ChatArtifacts";
import { ChatLoadingMessageBubble } from "./ChatLoadingMessageBubble";
import { ChatErrorMessageBubble } from "./ChatErrorMessageBubble";
import { useQueryFlags } from "scripts/hooks/useQueryFlags";
import { MessageActionBar } from "./MessageActionBar";
import Timestamp from "./Timestamp";
import { Operation } from "operations/operation";

export type Message = {
  id: string;
  timestamp?: string | Date;
  content: any;
  metadata?: any;
  message_id: number;
  role: "system" | "user" | "assistant" | "function" | "loading" | "error";
  rating?: number;
  error?: string;
  operation?: any;
  type?: 'user' | 'ai' | 'user-home' | 'ai-home' | 'error' | 'user-error';
};

export type Source = {
  url: string;
  title: string;
};

// Order matters in the user variant: rehypeRaw parses the raw HTML in the
// user's message into the tree, then rehypeSanitize prunes anything outside
// userSchema (scripts, iframes, event handlers, and every custom component
// except the filter chips). This is what blocks HTML/JSX injection from the
// textbox. Both arrays are module constants so the `rehypePlugins` prop does
// not churn on every render (see ChatArtifacts for why identity matters here).
const USER_REHYPE_PLUGINS = [rehypeRaw, [rehypeSanitize, userSchema]] as const;
const ASSISTANT_REHYPE_PLUGINS = [rehypeRaw] as const;

function ChatMessageBubbleComponent(props: {
  message: Message;
  aiEmoji?: string;
  isMostRecent: boolean;
  messageCompleted: boolean;
  onNewMessage: (message: string, operation?: any) => void;
  searchPrompt: string;
  operation?: Operation;
}) {
  const { displayErrorMsg } = useQueryFlags();
  const { message, onNewMessage, searchPrompt, operation } = props;
  const { role, content, metadata, error, type, rating, message_id } = message;
  const isUser = role === "user";

  // The artifact components are module constants; everything they need about
  // THIS message reaches them through context instead.
  const artifactContext = useMemo<IChatArtifactContext>(
    () => ({
      artifacts: metadata,
      onNewMessage,
      message: { message_id, rating, content, metadata },
    }),
    [metadata, onNewMessage, message_id, rating, content]
  );

  if (role === "loading") {
    return <ChatLoadingMessageBubble />;
  } else if (role === "error" && error) {
    return (
      <ChatErrorMessageBubble
        error={error}
        question={searchPrompt || ""}
        retry={onNewMessage}
        metadata={metadata || {}}
        operation={operation}
      />
    )
  } else if (type === "user-error" && displayErrorMsg) {
    // these are the possible class names for user-error and error type messages system-soft, admin-error, info-msg, user-msg
    return (
      <div className="d-flex align-items-center" style={{ maxWidth: '80%', marginLeft: "auto" }}>
        <Timestamp timestamp={message.timestamp} />
        <div className="user_bubble__text admin-error">{content}</div>
      </div>
    )
  } else if (type === "error" && displayErrorMsg) {
    return (
      <div className="d-flex align-items-center" style={{ maxWidth: '80%', marginRight: "auto" }}>
        <div className="user_bubble__text admin-error">{content}</div>
      </div>
    )
  }

  return (
    <ChatArtifactProvider value={artifactContext}>
      {/* User Message */}
      {isUser &&
        <div className="user_bubble__wraper withdate_stamp">
          <div className="user_bubble" style={{ maxWidth: '80%' }}>
            {metadata && metadata.userFilters ? (
              <>
                <Timestamp timestamp={message.timestamp} />
                <FilterPills filters={metadata.userFilters} />
              </>
            ) : (
              <>
                <Timestamp timestamp={message.timestamp} />
                <div className="user_bubble__text" style={{ minWidth: 0 }}>
                  <ReactMarkdown
                    rehypePlugins={USER_REHYPE_PLUGINS as any}
                    components={chatArtifactComponents as any}
                    children={message.content.replace(/^"|"$/g, "").replaceAll(/(^|[^\n])\n</g, "$1\n\n<")}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      }

      {/* Assistant Message */}
      {!isUser && (
        <div className="ai_bubble">
          {/* Render assistant's answer elements */}

          <div className="chatbot_bubble">
            <div className="chatbot_bubble__img">
              <img src="https://content.harstatic.com/media/icons/a_all/chat_ai_icon2.svg" />
            </div>
            <div className="chatbot_bubble__text swiper-slider-container">
              <ReactMarkdown
                rehypePlugins={ASSISTANT_REHYPE_PLUGINS as any}
                children={content.replaceAll(/(?<!\n)\n</g, "\n\n<")}
                components={chatArtifactComponents as any}
              />
            </div>
          </div>

          {!metadata?.search_listings && (
            <MessageActionBar
              messageId={message_id}
              initialRating={rating}
              content={content}
              metadata={metadata}
            />
          )}
        </div>
      )}
    </ChatArtifactProvider>
  );
}

/**
 * Memoized: an unrelated ChatWindow render (a keystroke in the composer, the
 * scroll-down button toggling, `isSending` flipping) re-rendered every bubble in
 * the transcript, and each of those renders used to remount every artifact it
 * contained. Props are compared shallowly, which is enough here — `message`
 * objects are replaced rather than mutated, and `onNewMessage` is already
 * identity-stable (ChatWindow binds it with an empty dependency list).
 *
 * This does NOT cover router-driven renders: `useQueryFlags` subscribes to the
 * location, so a navigation still re-renders every bubble. That is fine now that
 * a re-render no longer implies a remount.
 */
export const ChatMessageBubble = memo(ChatMessageBubbleComponent);
